import { type ReactNode, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppHeader, Nav } from './Nav';
import { BrandMark } from './BrandMark';
import { Button } from './Button';
import { NotificationsBell } from './NotificationsBell';
import { useAuth } from '@/auth/AuthContext';
import { useConfirm } from './Feedback';
import { getQueueCount, getFailedEvents } from '@/offline/queue';

import { defaultAdminPath, filterNavByPermission, type NavItemDef } from '@/pages/navItems';

interface LayoutProps {
  children: ReactNode;
  navItems: NavItemDef[];
  rightRail?: ReactNode;
  fullWidth?: boolean;
}

export function Layout({ children, navItems, rightRail, fullWidth }: LayoutProps) {
  const { hasPermission } = useAuth();
  const visibleNav = filterNavByPermission(navItems, hasPermission).map((item) => {
    if (item.to === '/app/admin' && item.label === 'Admin') {
      return { ...item, to: defaultAdminPath(hasPermission) };
    }
    return item;
  });
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  // Close the phone menu whenever the route changes.
  useEffect(() => setMenuOpen(false), [location.pathname]);

  return (
    <div className="app-layout">
      <AppHeader onMenuToggle={() => setMenuOpen((o) => !o)} menuOpen={menuOpen} />
      <div className="app-body">
        <aside className={`app-sidebar${menuOpen ? ' open' : ''}`} id="app-sidebar">
          <Nav items={visibleNav} variant="sidebar" />
        </aside>
        {menuOpen && <div className="app-sidebar-scrim" onClick={() => setMenuOpen(false)} aria-hidden />}
        <main className={`app-main ${fullWidth ? 'full' : ''}`}>
          {children}
        </main>
        {rightRail && <aside className="app-rail">{rightRail}</aside>}
      </div>
      <style>{`
        .app-layout {
          min-height: 100dvh;
          display: flex;
          flex-direction: column;
        }
        .app-body {
          display: flex;
          flex: 1;
          min-height: 0;
        }
        .app-sidebar {
          width: var(--sidebar-width);
          background: var(--color-surface);
          border-right: 1px solid var(--color-border);
          padding: var(--space-md);
          flex-shrink: 0;
        }
        .app-main {
          flex: 1;
          overflow: auto;
          min-width: 0;
        }
        .app-main.full {
          max-width: none;
        }
        .app-rail {
          width: var(--rail-width);
          background: var(--color-surface);
          border-left: 1px solid var(--color-border);
          padding: var(--space-md);
          flex-shrink: 0;
          overflow: auto;
        }
        @media (max-width: 1024px) {
          .app-rail { display: none; }
        }
        .app-sidebar-scrim { display: none; }
        @media (max-width: 768px) {
          .app-body { position: relative; }
          .app-sidebar {
            display: none;
            position: fixed;
            top: var(--header-height);
            left: 0;
            bottom: 0;
            width: min(280px, 85vw);
            overflow: auto;
            z-index: 900;
            box-shadow: var(--shadow-md);
          }
          .app-sidebar.open { display: block; }
          .app-sidebar-scrim {
            display: block;
            position: fixed;
            inset: var(--header-height) 0 0 0;
            background: rgba(14, 18, 22, 0.4);
            z-index: 899;
          }
        }
      `}</style>
    </div>
  );
}

function SessionBar({ variant }: { variant: 'crew' | 'portal' }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const confirm = useConfirm();

  const handleLogout = async () => {
    if (variant === 'crew') {
      // Signing out does not clear the phone's offline queue — it is sent after the
      // next sign-in with signal. Warn so crew don't assume the work already reached dispatch.
      let unsent = 0;
      try {
        unsent = (await getQueueCount()) + (await getFailedEvents()).length;
      } catch {
        unsent = 0;
      }
      if (unsent > 0) {
        const items = `${unsent} item${unsent === 1 ? '' : 's'}`;
        const ok = await confirm({
          title: 'Sign out with unsent work?',
          message: `${items} on this phone haven't reached dispatch yet. They stay saved here and will only send after you sign in again with signal. Best to wait for signal before signing out.`,
          confirmLabel: 'Sign out anyway',
          cancelLabel: 'Stay signed in',
          danger: true,
        });
        if (!ok) return;
      }
    }
    await logout();
    navigate('/login');
  };

  if (variant === 'crew') {
    const name = user?.name ?? user?.email ?? 'Signed in';
    const initial = name.trim().charAt(0).toUpperCase() || '?';
    return (
      <div className="crew-topbar">
        <span className="crew-avatar" aria-hidden>{initial}</span>
        <span className="crew-topbar-name">{name}</span>
        <button type="button" className="crew-topbar-signout" onClick={() => void handleLogout()}>
          Sign out
        </button>
        <style>{`
          .crew-topbar {
            display: flex;
            align-items: center;
            gap: var(--cs-3);
            min-height: 56px;
            padding: var(--cs-1) var(--cs-2) var(--cs-1) var(--crew-pad);
            border-bottom: 1px solid var(--crew-border);
            background: var(--crew-bg);
            min-width: 0;
          }
          .crew-avatar {
            width: 36px;
            height: 36px;
            border-radius: 50%;
            background: var(--crew-high);
            color: var(--crew-signal);
            font-weight: 700;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
          }
          .crew-topbar-name {
            flex: 1 1 auto;
            min-width: 0;
            font-weight: 600;
            font-size: var(--crew-fs-body);
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          .crew-topbar-signout {
            flex-shrink: 0;
            min-height: var(--crew-touch);
            padding: 0 var(--cs-3);
            background: none;
            border: none;
            border-radius: var(--crew-r);
            color: var(--crew-signal);
            font-weight: 700;
            font-size: var(--crew-fs-body);
            cursor: pointer;
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className={`session-bar session-bar-${variant}`}>
      <NotificationsBell />
      <span className="session-user">{user?.name ?? user?.email ?? 'Signed in'}</span>
      <Button
        variant="ghost"
        size="sm"
        onClick={handleLogout}
        className="session-signout"
      >
        Sign out
      </Button>
      <style>{`
        .session-bar {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: var(--space-sm);
          padding: var(--space-sm) var(--space-md);
          width: 100%;
          max-width: 100%;
          min-width: 0;
          box-sizing: border-box;
          overflow: hidden;
        }
        .session-bar-portal {
          margin-left: auto;
        }
        .session-user {
          font-size: 0.8125rem;
          opacity: 0.85;
          min-width: 0;
          flex: 1 1 auto;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          text-align: right;
        }
        .session-signout {
          flex-shrink: 0;
        }
        .session-bar-portal .btn-ghost {
          color: var(--color-white);
        }
      `}</style>
    </div>
  );
}

/**
 * Crew (driver) shell: top bar (avatar, name, Sign out); pages render the bottom tab bar
 * (Nav variant="crew") and, when they have one, a sticky CrewActionBar. Styles: styles/crew.css.
 */
export function CrewLayout({ children }: { children: ReactNode }) {
  return (
    <div className="crew-shell">
      <SessionBar variant="crew" />
      {children}
      <style>{`
        .crew-shell:has(.crew-scan-camera-mode) { padding-bottom: 0; }
      `}</style>
    </div>
  );
}

export function PortalLayout({ children }: { children: ReactNode }) {
  return (
    <div className="portal-shell">
      <header className="portal-header">
        <div className="portal-brand">
          <BrandMark variant="light" showLegal={false} size="sm" product="Portal" />
        </div>
        <SessionBar variant="portal" />
      </header>
      <main>{children}</main>
      <style>{`
        .portal-header {
          background: var(--color-ink);
          color: var(--color-white);
          padding: 0 var(--space-lg);
          display: flex;
          align-items: center;
          justify-content: space-between;
          min-height: 56px;
        }
        .portal-brand {
          display: flex;
          align-items: center;
        }
      `}</style>
    </div>
  );
}
